import { useState, useEffect } from 'react'
import axios from 'axios'
import { CloudOff, Droplets, Wind } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardAction } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { WEATHER_API_KEY, WEATHER_API_URL, WEATHER_CITY, WEATHER_COUNTRY } from '../config/weatherConfig'

function WeatherStat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <span className="flex size-9 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
        <Icon className="size-4" />
      </span>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="font-bold">{value}</p>
      </div>
    </div>
  )
}

function WeatherWidget() {
  const [weather, setWeather] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const response = await axios.get(WEATHER_API_URL, {
          params: {
            q: `${WEATHER_CITY},${WEATHER_COUNTRY}`,
            appid: WEATHER_API_KEY,
            units: 'metric',
            lang: 'pt_br',
          },
        })
        setWeather(response.data)
      } catch (err) {
        console.error('Erro ao buscar previsão do tempo:', err)
        setError('Não foi possível carregar a previsão do tempo agora.')
      } finally {
        setLoading(false)
      }
    }

    fetchWeather()
  }, [])

  if (loading) {
    return (
      <Card className="gap-6 py-6 [--card-spacing:--spacing(6)]">
        <CardHeader>
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-56" />
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <Skeleton className="h-4 w-32" />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Alert>
        <CloudOff />
        <AlertTitle>Previsão indisponível</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    )
  }

  if (!weather) return null

  return (
    <Card className="gap-6 [--card-spacing:--spacing(6)]">
      <CardHeader>
        <CardTitle className="text-2xl font-bold">
          {weather.name}, {weather.sys.country}
        </CardTitle>
        <CardDescription className="capitalize">
          {new Date().toLocaleDateString('pt-BR', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
          })}
        </CardDescription>
        <CardAction className="row-span-2 flex items-center">
          <img
            src={`https://openweathermap.org/img/wn/${weather.weather[0].icon}@2x.png`}
            alt={weather.weather[0].description}
            className="size-14"
          />
          <span className="font-heading text-4xl font-bold">
            {Math.round(weather.main.temp)}°
          </span>
        </CardAction>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <p className="text-muted-foreground capitalize">{weather.weather[0].description}</p>
        <Separator />
        <div className="grid grid-cols-2 gap-4">
          <WeatherStat icon={Droplets} label="Umidade" value={`${weather.main.humidity}%`} />
          <WeatherStat icon={Wind} label="Vento" value={`${weather.wind.speed} m/s`} />
        </div>
      </CardContent>
    </Card>
  )
}

export default WeatherWidget
